import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import {
  PoModalAction,
  PoModalComponent,
  PoPageAction,
  PoPageFilter,
  PoSelectOption,
  PoTableAction,
  PoTableColumn
} from '@po-ui/ng-components';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { MainService } from 'src/app/app.service';
import { NotificationServiceHM } from 'src/app/menu/notification-item/notification-item.service';
import { GitService } from '../../core/api/git.service';
import { GitRepository } from '../../core/interfaces/git.interface';
import { getApiErrorMessage, getHttpStatus } from '../../core/utils/http-error-message.util';
import { CostsAreasService } from '../costs/costs-areas.service';
import { parseRepositoryUrl, REPOSITORY_URL_PATTERN } from './repository-url';
import { AdminGitProjectComboFilterService } from './admin-git-project-combo-filter.service';
import { AdminGitRepositorySearchService } from './admin-git-repository-search.service';
import {
  CreateRepositoryRequest,
  RepositoryListParams,
  SmartReviewRepository,
  UpdateRepositoryRequest
} from './interfaces/repositories.interface';
import { RepositoriesService } from './repositories.service';
import { sourceProviderLabel } from 'src/app/menu/smart-review/core/utils/provider-label.util';

type RepositoryModalMode = 'create' | 'edit';

/** Tamanho da página pedida à API; o teto dela é 200. */
export const REPOSITORY_PAGE_SIZE = 50;

interface RepositoryForm {
  provider: string;
  externalId: string;
  org: string;
  project: string;
  name: string;
  defaultBranch: string;
  areaId: string;
  enabled: boolean;
}

interface RepositoryRow extends SmartReviewRepository {
  fullName: string;
  providerLabel: string;
  statusLabel: string;
  statusType: string;
  areaLabel: string;
}

@Component({
  selector: 'app-admin-repositories',
  templateUrl: './admin-repositories.component.html',
  styleUrls: ['./admin-repositories.component.css']
})
export class AdminRepositoriesComponent implements OnInit {
  @ViewChild('repositoryModal', { static: true }) repositoryModal!: PoModalComponent;
  @ViewChild('deleteModal', { static: true }) deleteModal!: PoModalComponent;

  /** Linhas já carregadas, página a página, com o filtro aplicado pelo servidor. */
  rows: RepositoryRow[] = [];
  /** Quantos cadastrados casam com o filtro no servidor. */
  total = 0;
  loading = false;
  loadingShowMore = false;
  saving = false;
  deleting = false;
  searchTerm = '';
  statusFilter = '';
  areaFilter = '';
  modalMode: RepositoryModalMode = 'create';
  editing: SmartReviewRepository | null = null;
  pendingDelete: RepositoryRow | null = null;
  form: RepositoryForm = emptyRepositoryForm();

  /**
   * Endereço colado por quem cadastra, e o que foi lido dele.
   *
   * O campo é ATALHO: preenche as coordenadas e some do caminho. Não é obrigatório, não
   * é enviado ao servidor, e o que ele preenche continua editável — quem prefere digitar
   * org e nome ignora o campo e nada muda.
   */
  repositoryUrl = '';
  repositoryUrlResolved = '';
  readonly repositoryUrlPattern = REPOSITORY_URL_PATTERN;
  areaOptions: PoSelectOption[] = [{ label: 'Todas as áreas', value: '' }];
  private areaNamesMap = new Map<string, string>();
  private listSubscription?: Subscription;

  /**
   * Provedores configurados e repositórios que eles enxergam, para o cadastro ser
   * escolhido em vez de digitado.
   *
   * A identidade técnica fica nos bastidores: quando o provedor sabe navegar, a
   * listagem traz o `externalId` correto e o usuário vê só o caminho humano. Onde o
   * provedor ainda não sabe listar, o cadastro manual continua disponível.
   */
  providerOptions: PoSelectOption[] = [];
  loadingProviders = false;
  remoteLookupError = '';
  selectedRemoteRepo = '';
  readonly remoteProjectComboFilterService = new AdminGitProjectComboFilterService(this.gitService);
  readonly remoteRepoSearchService = new AdminGitRepositorySearchService(this.gitService);

  selectedRemoteProject = '';
  remoteProjectLookupError = '';
  private providerBrowsable = new Map<string, boolean>();
  private providerProjectScoped = new Map<string, boolean>();

  readonly statusFilterOptions: PoSelectOption[] = [
    { label: 'Todos', value: '' },
    { label: 'Habilitados', value: 'enabled' },
    { label: 'Desabilitados', value: 'disabled' }
  ];

  readonly filter: PoPageFilter = {
    action: this.onQuickSearch.bind(this),
    // A área saiu da busca: ela é filtrada pelo combo ao lado, e no servidor o termo casa
    // só com os campos do cadastro.
    placeholder: 'Buscar por repositório ou provedor',
    width: 4
  };

  /**
   * Toda coluna tem largura explícita, como na lista de reviews.
   *
   * `fullName` era a única sem, e o po-table dá a ela todo o espaço que sobra: num monitor
   * largo o nome do repositório ficava numa coluna de centenas de pixels quase vazia
   * enquanto Provider, Área e Status se apertavam na borda direita.
   *
   * A largura dela é a maior porque carrega `dono/nome`, que é o valor mais longo da linha.
   */
  readonly columns: PoTableColumn[] = [
    { property: 'fullName', label: 'Repositório', width: '320px' },
    { property: 'providerLabel', label: 'Provedor', width: '160px' },
    { property: 'areaLabel', label: 'Área', width: '160px' },
    { property: 'statusLabel', label: 'Status', type: 'cellTemplate', width: '130px' },
    { property: 'updatedAt', label: 'Atualizado em', type: 'dateTime', width: '170px' }
  ];

  readonly pageActions: PoPageAction[] = [
    { label: 'Novo repositório', icon: 'po-icon-plus', action: () => this.openCreate() },
    { label: 'Atualizar', icon: 'po-icon-refresh', action: () => this.loadRepositories() }
  ];

  readonly tableActions: PoTableAction[] = [
    { label: 'Editar', icon: 'po-icon-edit', action: this.openEdit.bind(this) },
    {
      label: 'Habilitar',
      icon: 'po-icon-ok',
      visible: (row: RepositoryRow) => !row.enabled,
      action: (row: RepositoryRow) => this.setEnabled(row, true)
    },
    {
      label: 'Desabilitar',
      icon: 'po-icon-close',
      visible: (row: RepositoryRow) => row.enabled,
      action: (row: RepositoryRow) => this.setEnabled(row, false)
    },
    { label: 'Excluir', icon: 'po-icon-delete', type: 'danger', action: this.openDelete.bind(this) }
  ];

  saveAction: PoModalAction = { label: 'Salvar', action: () => this.save() };
  cancelAction: PoModalAction = { label: 'Cancelar', action: () => this.repositoryModal.close() };
  deletePrimaryAction: PoModalAction = { label: 'Excluir', danger: true, action: () => this.deletePending() };
  deleteSecondaryAction: PoModalAction = { label: 'Cancelar', action: () => this.closeDeleteModal() };

  constructor(
    private repositoriesService: RepositoriesService,
    private gitService: GitService,
    private areasService: CostsAreasService,
    private mainService: MainService,
    private notification: NotificationServiceHM,
    private router: Router
  ) {
    this.remoteProjectComboFilterService.notify = error => { this.remoteProjectLookupError = error; };
    this.remoteRepoSearchService.notify = error => { this.remoteLookupError = error; };
  }

  ngOnInit(): void {
    this.mainService.validSession();
    this.loadAreaNames();
    this.loadProviders();
    this.loadRepositories();
  }

  /** Provedores configurados. Só os `browsable` sabem listar repositórios. */
  private loadProviders(): void {
    this.loadingProviders = true;
    this.gitService.listProviders().subscribe({
      next: providers => {
        this.providerBrowsable = new Map(providers.map(p => [this.clean(p.provider), p.browsable]));
        this.providerProjectScoped = new Map(providers.map(p => [this.clean(p.provider), p.projectScoped === true]));
        // O rótulo é o do produto, o valor é o identificador do adaptador: quem escolhe
        // lê "Azure DevOps", e o que viaja para a API continua sendo `azuredevops`.
        this.providerOptions = providers.map(p => ({ label: sourceProviderLabel(p.provider), value: p.provider }));
        this.configureRemoteFilters();
        this.loadingProviders = false;
      },
      error: () => { this.loadingProviders = false; }
    });
  }

  /**
   * O projeto escolhido recorta a busca de repositório.
   *
   * A etapa existe só nos provedores escopados por projeto, e o motivo é custo, não
   * exigência da API: no Azure DevOps buscar sem projeto faz o adaptador resolver o grupo
   * de cada projeto da organização e listar os repositórios de cada um acessível. Ver a
   * conta em AdminGitRepositorySearchService.
   */
  onRemoteProjectSelected(projectName: string | number): void {
    this.selectedRemoteProject = this.clean(projectName);
    const project = this.remoteProjectComboFilterService.projectFor(this.selectedRemoteProject);
    this.form.project = project?.name || this.selectedRemoteProject;
    this.selectedRemoteRepo = '';
    this.remoteLookupError = '';
    this.remoteRepoSearchService.project = this.selectedRemoteProject;
    this.remoteRepoSearchService.clearRepositories();
  }

  /**
   * Preenche a origem a partir do que o provedor respondeu.
   */
  onRemoteRepoSelected(fullName: string | number): void {
    this.selectedRemoteRepo = this.clean(fullName);
    const remote = this.remoteRepoSearchService.repositoryFor(this.selectedRemoteRepo);
    if (!remote) { return; }

    this.form.org = remote.owner || '';
    this.form.project = remote.project || '';
    this.form.name = remote.name || '';
    this.form.externalId = this.externalIdFor(remote);
    this.form.defaultBranch = remote.defaultBranch || '';
    if (remote.registered) {
      this.notification.showToast('Este repositório já está cadastrado.', 'warning');
    }
  }

  onProviderChange(provider: string | number): void {
    this.form.provider = this.clean(provider);
    this.resetRemoteLookup();
    this.configureRemoteFilters();
  }

  /**
   * Identidade do repositório retornada pela navegação do provedor.
   *
   * O projeto entra quando existe: em provedores com essa dimensão, omiti-lo faria dois
   * repositórios homônimos em projetos diferentes colidirem.
   */
  private externalIdFor(remote: GitRepository): string {
    const providerExternalId = this.clean(remote.externalId);
    if (providerExternalId) {
      return providerExternalId;
    }

    const parts = [remote.owner, remote.project, remote.name].map(p => (p || '').trim()).filter(Boolean);
    return parts.join('/');
  }

  get modalTitle(): string {
    return this.modalMode === 'create' ? 'Novo repositório' : `Editar ${this.editing ? this.repositoryFullName(this.editing) : ''}`;
  }

  get editingMode(): boolean {
    return this.modalMode === 'edit';
  }

  get canSearchRemoteRepos(): boolean {
    const provider = this.clean(this.form.provider);
    if (!provider) {
      return false;
    }
    return this.providerBrowsable.get(provider) === true;
  }

  get selectedProviderProjectScoped(): boolean {
    const provider = this.clean(this.form.provider);
    return provider !== '' && this.providerProjectScoped.get(provider) === true;
  }

  /**
   * A busca de repositório espera o projeto nos provedores que o têm.
   *
   * Não é enfeite: sem projeto, cada busca no Azure DevOps custa uma chamada por projeto
   * da organização. O campo fica desabilitado com o placeholder dizendo o que falta.
   */
  get remoteRepoLookupDisabled(): boolean {
    return this.selectedProviderProjectScoped && this.clean(this.selectedRemoteProject) === '';
  }

  get remoteRepoPlaceholder(): string {
    if (this.remoteRepoLookupDisabled) {
      return 'Escolha o projeto primeiro';
    }
    return this.selectedProviderProjectScoped
      ? 'Digite pelo menos 2 caracteres do nome'
      : 'Digite o nome, ou dono/nome';
  }

  get selectedProviderCannotBrowse(): boolean {
    const provider = this.clean(this.form.provider);
    return provider !== '' && !this.canSearchRemoteRepos;
  }

  /**
   * Dica do cadastro manual.
   *
   * Não diz "ainda não possui": no GitHub isso não é pendência, é limitação permanente da
   * REST API — ela não sabe dizer QUAIS repositórios um terceiro enxerga, e listar com a
   * credencial de serviço mostraria aqui o nome de todo repositório privado da
   * organização. "Ainda" prometia uma busca que não vai chegar.
   */
  get manualRegistrationHint(): string {
    return 'Este provedor não permite buscar repositórios em nome do usuário. Informe as coordenadas do repositório.';
  }

  /**
   * As coordenadas aparecem em todo cadastro, e não só onde o provedor não navega.
   *
   * Antes elas eram o caminho ALTERNATIVO: quem cadastrava num provedor com busca via só
   * os combos, e uma listagem que falhasse — token sem escopo, provedor fora do ar —
   * deixava a tela sem saída, com um aviso e nenhum campo para preencher. O fluxo passa a
   * ser um só, e a busca vira atalho para ele.
   */
  get showManualIdentityFields(): boolean {
    return !this.editingMode;
  }

  /**
   * Exemplo de endereço no formato do provedor escolhido.
   *
   * Mostrar `github.com` a quem cadastra no Azure DevOps ensina o formato errado, e o
   * formato é justamente o que muda entre eles.
   */
  get repositoryUrlPlaceholder(): string {
    switch (this.clean(this.form.provider)) {
      case 'azuredevops':
        return 'Cole a URL — https://dev.azure.com/org/projeto/_git/repo';
      case 'gitea':
        return 'Cole a URL — https://gitea.local/org/repo';
      case 'bitbucket-cloud':
        return 'Cole a URL — https://bitbucket.org/workspace/repo';
      default:
        return 'Cole a URL — https://github.com/org/repo';
    }
  }


  /**
   * Só há o que perguntar sobre a origem depois de escolhido o provedor.
   *
   * O formato do endereço, a existência de projeto e a própria oferta de busca mudam com
   * ele: mostrar "Cole a URL — https://github.com/org/repo" antes da escolha ensina um
   * formato que pode não ser o do provedor, e "Org" e "Nome" abrem sem que se saiba o que
   * significam ali. A tela começa com uma pergunta só, e cresce conforme é respondida.
   */
  get showOriginFields(): boolean {
    return this.clean(this.form.provider) !== '';
  }

  /**
   * Há repositório escolhido o bastante para falar de configuração.
   *
   * Mede o DADO, e não a visibilidade dos campos: com o fluxo unificado as coordenadas
   * aparecem desde o início, e amarrar a isso faria a seção de configuração surgir antes
   * de existir repositório — inclusive quando só o projeto foi escolhido, no Azure DevOps.
   */
  get hasRepositoryIdentity(): boolean {
    return this.editingMode
      || this.clean(this.form.name) !== ''
      || this.clean(this.form.externalId) !== '';
  }

  get deleteTitle(): string {
    return this.pendingDelete ? `Excluir ${this.pendingDelete.fullName}?` : 'Excluir repositório?';
  }

  get allLoaded(): boolean {
    return this.rows.length >= this.total;
  }

  /**
   * Recarrega a partir da primeira página, com o filtro atual.
   *
   * A resposta anterior ainda em voo é descartada: com a busca digitada, a página de um
   * termo velho chegando depois da do novo trocaria a tabela pelo recorte errado.
   */
  loadRepositories(): void {
    this.listSubscription?.unsubscribe();
    this.loading = true;
    this.loadingShowMore = false;
    this.listSubscription = this.repositoriesService.listRepositories(this.listParams(0)).subscribe({
      next: page => {
        this.rows = page.items.map(repo => this.toRow(repo));
        this.total = page.total;
        this.loading = false;
      },
      error: error => {
        this.loading = false;
        this.handleAdminError(error, 'Erro ao carregar repositórios.');
      }
    });
  }

  /**
   * Próxima página. O offset é o número de linhas já carregadas, e não página × tamanho:
   * excluir ou desabilitar tira linhas da tabela e do total, e contar por página pularia
   * cadastros. Id repetido é ignorado porque outro administrador pode cadastrar um
   * repositório que, pela ordem de slug, cai antes do que já foi carregado — e empurra
   * para a página seguinte uma linha que já está na tela.
   */
  onShowMore(): void {
    if (this.allLoaded || this.loadingShowMore || this.loading) { return; }
    this.loadingShowMore = true;
    this.listSubscription = this.repositoriesService.listRepositories(this.listParams(this.rows.length)).subscribe({
      next: page => {
        const known = new Set(this.rows.map(row => row.id));
        this.rows = [...this.rows, ...page.items.filter(repo => !known.has(repo.id)).map(repo => this.toRow(repo))];
        this.total = page.total;
        this.loadingShowMore = false;
      },
      error: error => {
        this.loadingShowMore = false;
        this.handleAdminError(error, 'Erro ao carregar repositórios.');
      }
    });
  }

  private listParams(offset: number): RepositoryListParams {
    return {
      q: this.clean(this.searchTerm),
      status: this.clean(this.statusFilter) as RepositoryListParams['status'],
      areaId: this.clean(this.areaFilter),
      limit: REPOSITORY_PAGE_SIZE,
      offset
    };
  }

  private loadAreaNames(): void {
    this.areasService.areaNames().subscribe(names => {
      this.areaNamesMap = names;
      this.areaOptions = [
        { label: 'Todas as áreas', value: '' },
        ...CostsAreasService.toOptions(names)
      ];
      this.rows = this.rows.map(row => this.toRow(row));
    });
  }

  onRepositoryFiltersChange(): void {
    this.loadRepositories();
  }

  onQuickSearch(term: string): void {
    this.searchTerm = this.clean(term);
    this.loadRepositories();
  }

  /**
   * Lê o endereço colado e preenche as coordenadas.
   *
   * Endereço que não se reconhece não apaga o que já está preenchido: quem colou algo
   * inesperado perderia o que tinha digitado antes, e o campo existe para poupar
   * trabalho, não para criar.
   */
  onRepositoryUrlChange(value: string): void {
    this.repositoryUrl = this.clean(value);
    const parsed = parseRepositoryUrl(this.repositoryUrl);
    if (!parsed) {
      this.repositoryUrlResolved = '';
      return;
    }
    this.form.org = parsed.org;
    this.form.name = parsed.name;
    // O projeto só é sobrescrito quando o endereço traz um: no GitHub ele vem vazio, e
    // zerar um valor que a pessoa digitou seria destruir o que ela fez à mão.
    if (parsed.project !== '') {
      this.form.project = parsed.project;
    }
    this.repositoryUrlResolved = [parsed.org, parsed.project, parsed.name].filter(Boolean).join(' / ');
  }

  openCreate(): void {
    this.modalMode = 'create';
    this.editing = null;
    this.form = emptyRepositoryForm();
    this.resetRemoteLookup();
    this.syncModalActions();
    this.repositoryModal.open();
  }

  openEdit(row: RepositoryRow): void {
    this.modalMode = 'edit';
    this.editing = row;
    this.form = formFromRepository(row);
    this.resetRemoteLookup();
    this.syncModalActions();
    this.repositoryModal.open();
  }

  save(): void {
    if (this.saving || !this.validateForm()) { return; }

    this.saving = true;
    this.syncModalActions();
    const request = this.editingMode
      ? this.repositoriesService.updateRepository(this.editing!.id, this.updateBody())
      : this.repositoriesService.createRepository(this.createBody());

    request.pipe(finalize(() => {
      this.saving = false;
      this.syncModalActions();
    })).subscribe({
      next: repo => {
        // Cadastro novo recarrega: é o servidor que sabe se ele casa com o filtro e em que
        // página cai pela ordem de slug.
        if (this.editingMode) {
          this.upsertRow(repo);
        } else {
          this.loadRepositories();
        }
        this.repositoryModal.close();
        this.notification.showToast('Repositório salvo com sucesso.', 'success');
      },
      error: error => this.handleAdminError(error, 'Erro ao salvar repositório.')
    });
  }

  setEnabled(row: RepositoryRow, enabled: boolean): void {
    if (this.saving) { return; }

    this.saving = true;
    this.repositoriesService.updateRepository(row.id, { enabled })
      .pipe(finalize(() => { this.saving = false; }))
      .subscribe({
        next: repo => {
          this.upsertRow(repo);
          this.notification.showToast(
            enabled ? 'Repositório habilitado com sucesso.' : 'Repositório desabilitado com sucesso.',
            'success'
          );
        },
        error: error => this.handleAdminError(error, 'Erro ao atualizar repositório.')
      });
  }

  openDelete(row: RepositoryRow): void {
    this.pendingDelete = row;
    this.syncDeleteActions();
    this.deleteModal.open();
  }

  closeDeleteModal(): void {
    this.pendingDelete = null;
    this.deleteModal.close();
  }

  deletePending(): void {
    if (!this.pendingDelete || this.deleting) { return; }

    const id = this.pendingDelete.id;
    this.deleting = true;
    this.syncDeleteActions();
    this.repositoriesService.deleteRepository(id)
      .pipe(finalize(() => {
        this.deleting = false;
        this.syncDeleteActions();
      }))
      .subscribe({
        next: () => {
          this.removeRow(id);
          this.pendingDelete = null;
          this.deleteModal.close();
          this.notification.showToast('Repositório excluído com sucesso.', 'success');
        },
        error: error => this.handleAdminError(error, 'Erro ao excluir repositório.')
      });
  }

  private resetRemoteLookup(): void {
    this.repositoryUrl = '';
    this.repositoryUrlResolved = '';
    this.selectedRemoteProject = '';
    this.remoteProjectLookupError = '';
    this.selectedRemoteRepo = '';
    this.remoteLookupError = '';
    this.remoteProjectComboFilterService.reset();
    this.remoteRepoSearchService.reset();
  }

  private configureRemoteFilters(): void {
    const provider = this.clean(this.form.provider);
    this.remoteProjectComboFilterService.provider = provider;
    this.remoteRepoSearchService.provider = provider;
    this.remoteRepoSearchService.project = this.selectedRemoteProject;
    // Dois caracteres onde a busca varre a organização; nenhum onde ela custa uma
    // chamada e a lista pode simplesmente aparecer.
    this.remoteRepoSearchService.minLength = this.selectedProviderProjectScoped ? 2 : 0;
  }

  /**
   * A validação cobra o DADO, e não o caminho por onde ele chegou.
   *
   * Ela exigia que um repositório tivesse sido escolhido na busca sempre que o provedor
   * soubesse listar — resíduo de quando a busca era o único caminho. Com o fluxo
   * unificado isso passou a contradizer a própria tela: quem colava a URL ou digitava as
   * coordenadas preenchia tudo e era barrado no Salvar, com um aviso pedindo que usasse
   * um campo que ele deliberadamente não usou. Era o beco sem saída que o fluxo unificado
   * existia para fechar, sobrevivendo na validação.
   *
   * Org e projeto entram aqui porque o servidor os exige, e a mensagem dele chegaria como
   * 400 sem dizer em qual campo mexer.
   */
  private validateForm(): boolean {
    if (this.clean(this.form.provider) === '') {
      this.notification.showToast('Informe o provider.', 'warning');
      return false;
    }
    if (this.editingMode) {
      return true;
    }
    if (this.clean(this.form.name) === '') {
      this.notification.showToast('Informe o nome do repositório.', 'warning');
      return false;
    }
    if (this.clean(this.form.org) === '') {
      this.notification.showToast('Informe a org do repositório.', 'warning');
      return false;
    }
    if (this.selectedProviderProjectScoped && this.clean(this.form.project) === '') {
      this.notification.showToast('Informe o projeto: neste provedor ele faz parte da identidade do repositório.', 'warning');
      return false;
    }
    return true;
  }

  private createBody(): CreateRepositoryRequest {
    return {
      provider: this.clean(this.form.provider),
      externalId: this.optional(this.form.externalId),
      org: this.optional(this.form.org),
      project: this.optional(this.form.project),
      name: this.clean(this.form.name),
      defaultBranch: this.optional(this.form.defaultBranch),
      areaId: this.optional(this.form.areaId)
    };
  }

  private updateBody(): UpdateRepositoryRequest {
    return {
      defaultBranch: this.clean(this.form.defaultBranch),
      areaId: this.clean(this.form.areaId),
      enabled: this.form.enabled
    };
  }

  /**
   * Aplica uma edição à linha carregada.
   *
   * Editar só mexe em status, área e branch — o termo da busca casa com campos que não
   * mudam depois do cadastro. Então basta conferir status e área: se a edição tirou o
   * repositório do recorte (desabilitar com o filtro "Habilitados"), ele sai da tabela e
   * do total, como o servidor faria no próximo carregamento.
   */
  private upsertRow(repo: SmartReviewRepository): void {
    const next = this.toRow(repo);
    if (!this.matchesEditableFilters(next)) {
      this.removeRow(next.id);
      return;
    }
    this.rows = this.rows.map(row => row.id === next.id ? next : row);
  }

  private matchesEditableFilters(row: RepositoryRow): boolean {
    const status = this.clean(this.statusFilter);
    const area = this.clean(this.areaFilter);
    const statusMatches = !status || (status === 'enabled' ? row.enabled : !row.enabled);
    const areaMatches = !area || (row.areaId ?? '') === area;
    return statusMatches && areaMatches;
  }

  private removeRow(id: number): void {
    const before = this.rows.length;
    this.rows = this.rows.filter(row => row.id !== id);
    if (this.rows.length < before) {
      this.total = Math.max(this.total - 1, 0);
    }
  }

  private toRow(repo: SmartReviewRepository): RepositoryRow {
    return {
      ...repo,
      fullName: this.repositoryFullName(repo),
      providerLabel: sourceProviderLabel(repo.provider),
      statusLabel: repo.enabled ? 'Habilitado' : 'Desabilitado',
      statusType: repo.enabled ? 'success' : 'warning',
      areaLabel: repo.areaId ? CostsAreasService.label(repo.areaId, this.areaNamesMap) : 'Todas'
    };
  }

  private syncModalActions(): void {
    this.saveAction = {
      label: 'Salvar',
      action: () => this.save(),
      disabled: this.saving,
      loading: this.saving
    };
  }

  private syncDeleteActions(): void {
    this.deletePrimaryAction = {
      label: 'Excluir',
      danger: true,
      action: () => this.deletePending(),
      disabled: this.deleting,
      loading: this.deleting
    };
  }

  private clean(value: unknown): string {
    return String(value ?? '').trim();
  }

  private optional(value: unknown): string | undefined {
    const cleaned = this.clean(value);
    return cleaned || undefined;
  }

  private repositoryFullName(repo: Pick<SmartReviewRepository, 'org' | 'project' | 'name'>): string {
    return [repo.org, repo.project, repo.name]
      .map(value => this.clean(value))
      .filter(Boolean)
      .join('/') || this.clean(repo.name);
  }

  private handleAdminError(error: unknown, fallback: string): void {
    const status = getHttpStatus(error);
    if (status === 403) {
      this.notification.showToast('Acesso à administração de repositórios negado.', 'warning');
      this.router.navigate(['/menu/smart-review/reviews']);
      return;
    }
    if (status === 400 || status === 409) {
      this.notification.showToast(getApiErrorMessage(error, fallback), 'warning');
      return;
    }
    this.notification.showToast(getApiErrorMessage(error, fallback), 'error');
  }
}

function emptyRepositoryForm(): RepositoryForm {
  return {
    provider: '',
    externalId: '',
    org: '',
    project: '',
    name: '',
    defaultBranch: '',
    areaId: '',
    enabled: true
  };
}

function formFromRepository(repo: SmartReviewRepository): RepositoryForm {
  return {
    provider: repo.provider,
    externalId: repo.externalId,
    org: repo.org ?? '',
    project: repo.project ?? '',
    name: repo.name,
    defaultBranch: repo.defaultBranch ?? '',
    areaId: repo.areaId ?? '',
    enabled: repo.enabled
  };
}


